import { useEffect, useMemo, useState } from "react";
import { useProjects } from "@contexts/ProjectContext";
import type { Project } from "@shared/types";
import { aiReviewRepository, type AIReviewDoc } from "@data/firestore/AIReviewRepository";
import type { AIObservation, Citation, CitationType } from "@data/functions/projectReviewer";
import { useRejectedObservations } from "./useRejectedObservations";
import { observationFingerprint } from "@shared/utils/observationFingerprint";

interface ProjectContextValue {
  activeProject: Project | null | undefined;
}

export interface UseLatestAIReviewResult {
  review: AIReviewDoc | null;
  loading: boolean;
  /** All observations on the latest review, regardless of citation. */
  observations: AIObservation[];
  /** Map of citation type → itemId (as string) → observations that cite it. */
  byItem: Map<CitationType, Map<string, AIObservation[]>>;
  /** Observations citing an entity of the given type with the given id. */
  observationsFor(type: CitationType, itemId: string | number): AIObservation[];
  /** Observations with only a "general" citation (not tied to a specific item). */
  generalObservations: AIObservation[];
}

/**
 * Subscribes to the latest AI review for the active project and organizes
 * its observations by citation for inline UI ("!" on Budget rows, Timeline
 * phases, etc.).
 */
export function useLatestAIReview(): UseLatestAIReviewResult {
  const projects = useProjects() as ProjectContextValue;
  const activeProject = projects.activeProject;
  const [review, setReview] = useState<AIReviewDoc | null>(null);
  const [loading, setLoading] = useState(false);
  const { isRejected } = useRejectedObservations();

  useEffect(() => {
    if (!activeProject) {
      setReview(null);
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    const unsub = aiReviewRepository.watchLatest(activeProject, (next) => {
      setReview(next);
      setLoading(false);
    });
    return unsub;
  }, [activeProject]);

  // Filter out observations the user has explicitly rejected. Belt +
  // suspenders with the prompt-side exclusion in the flow.
  const observations = useMemo(
    () =>
      (review?.observations ?? []).filter(
        (o) => !isRejected(observationFingerprint(o.title))
      ),
    [review, isRejected]
  );

  const byItem = useMemo(() => {
    const map = new Map<CitationType, Map<string, AIObservation[]>>();
    for (const obs of observations) {
      const citations: Citation[] = obs.citations ?? [];
      for (const cite of citations) {
        if (cite.type === "general" || cite.itemId == null) continue;
        const key = String(cite.itemId);
        if (!map.has(cite.type)) {
          map.set(cite.type, new Map());
        }
        const typeMap = map.get(cite.type)!;
        if (!typeMap.has(key)) typeMap.set(key, []);
        typeMap.get(key)!.push(obs);
      }
    }
    return map;
  }, [observations]);

  const observationsFor = (type: CitationType, itemId: string | number) => {
    const m = byItem.get(type);
    if (!m) return [];
    return m.get(String(itemId)) ?? [];
  };

  const generalObservations = useMemo(
    () =>
      observations.filter((o) =>
        (o.citations ?? []).every((c) => c.type === "general" || c.itemId == null)
      ),
    [observations]
  );

  return {
    review,
    loading,
    observations,
    byItem,
    observationsFor,
    generalObservations,
  };
}
